import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import SavedIndicator from '@/components/application_components/SavedIndicator';
import CommentsWrap from '@/components/comments_components/CommentsWrap';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import useAutosave from '@/hooks/useAutosave';
import { Currency } from '@/models/Currency';
import { ProgramNames } from '@/models/Program';
import { QuestionAlias, QuestionProps } from '@/models/Question';
import { QuestionChoiceProps } from '@/models/QuestionChoice';
import { StandardQuestionDetails } from '@/models/Standard';
import { maxDecimalValue } from '@/utils/numberUtil';
import MaxValueExceededWarning from './MaxValueExceededWarning';
import useNumberFormatter from '@/hooks/useNumberFormatter';
import { logError } from '@/utils/errorHandlerUtil';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';

function MoneyTypeQuestion({ question }: { question: QuestionProps }) {
  const { answers, canEdit, saveApplicationAnswer2, questions, currentStandardId } =
    useContext(CurrentApplicationContext);
  const { translatedText } = useContext(LocaleContext);
  const { formatter, parseCurrency } = useNumberFormatter();
  const { setAlert } = useContext(SnackbarContext);

  const { currencies, selectedProgramId } = useContext(SelectedProgramContext);

  const questionsInStandard = useMemo(
    () =>
      questions.standards.find((st: StandardQuestionDetails) => st.standardId == currentStandardId)
        ?.questions ?? [],
    [questions, currentStandardId]
  );

  const currency: Currency | null = useMemo(() => {
    const currencyQuestion = questionsInStandard.find(
      (q) => q.alias == QuestionAlias.TCOW_Expenditure_Currency.toString()
    );

    if (currencyQuestion == null) return null;

    const currencyQuestionChoice = answers[currencyQuestion.id]?.choices[0];

    if (currencyQuestion?.questionChoices == null || currencyQuestionChoice == null) return null;

    const currencyChoice: string | undefined = currencyQuestion.questionChoices.find(
      (choice: QuestionChoiceProps) => choice.choiceId == currencyQuestionChoice.choiceId
    )?.choice;

    if (currencyChoice == null) return null;

    return currencies.find((curr) => currencyChoice.includes(curr.name)) ?? null;
  }, [answers, currencies, questionsInStandard]);

  const questionAnswer = answers[question.id];
  const questionAnswerValue =
    questionAnswer != null ? (questionAnswer.responseDecimal ?? questionAnswer.responseInt) : null;
  const formattedQuestionAnswerValue =
    questionAnswerValue !== null ? formatter.format(questionAnswerValue) : '';
  const [currentValue, setCurrentValue] = useState(formattedQuestionAnswerValue);
  const { numberValue, valid: validFormat } = parseCurrency(currentValue);

  const handleFocused = () => {
    setCurrentValue(validFormat ? (numberValue === null ? '' : String(numberValue)) : currentValue);
  };

  const handleBlurred = () => {
    const { numberValue: parsed, valid: parsedValid } = parseCurrency(currentValue);
    setCurrentValue(parsedValid ? (parsed === null ? '' : formatter.format(parsed)) : currentValue);
  };

  const [saveFailed, setSaveFailed] = useState(false);

  const maxValue = maxDecimalValue;
  const maxValueExceededWarning = translatedText.decimalQuestion.maxValueExceeded;

  const missingRequiredValue = question.questionNeeded && currentValue === '';
  const negativeValue = numberValue !== null && numberValue < 0;
  const exceededMaxValue = maxValue !== null && numberValue !== null && numberValue > maxValue;
  const outOfRangeValue = negativeValue || exceededMaxValue;
  const saveableValue = !(outOfRangeValue || !validFormat);

  const hasError = missingRequiredValue || outOfRangeValue || !validFormat;

  const saveValue = useCallback(
    async (v: number | null): Promise<void> => {
      setSaveFailed(false);
      try {
        await saveApplicationAnswer2(question, v);
      } catch (error) {
        logError(`Error saving response in money type question.`, error);
        setAlert({
          severity: 'error',
          message: 'Error saving response - please try again.',
        });
        setSaveFailed(true);
      }
    },
    [question, saveApplicationAnswer2, setAlert]
  );

  const { saved, isSaving, markTouched, triggerSave } = useAutosave<number | null>(
    numberValue,
    async (v) => {
      if (!saveableValue) return;
      return await saveValue(v);
    },
    1500
  );

  const handleChanged = (e: ChangeEvent<HTMLInputElement>) => {
    markTouched();
    setCurrentValue(e.target.value);
  };

  return (
    <Box
      display='flex'
      width='100%'
      justifyContent='space-between'
      alignItems='start'
      id={`money-question-${question.id}`}
    >
      <Box display='flex' flexDirection='column' justifyContent='flex-start' gap='4px' flexGrow={0}>
        <Box display='flex' flexDirection='row' alignItems='center'>
          <TextField
            id={question.id.toString()}
            label={question.questionText}
            sx={{
              minWidth: '500px',
              maxWidth: '500px',
            }}
            type={'text'}
            slotProps={{
              input: {
                startAdornment:
                  selectedProgramId != ProgramNames.TreeCitiesWorld ? (
                    <InputAdornment position='start'>$</InputAdornment>
                  ) : null,
                endAdornment:
                  currency != null ? (
                    <InputAdornment position='end'>{currency.isoCode}</InputAdornment>
                  ) : null,
              },
            }}
            variant='outlined'
            disabled={!canEdit}
            onFocus={handleFocused}
            onBlur={handleBlurred}
            onChange={handleChanged}
            required={question.questionNeeded}
            value={currentValue}
            error={hasError}
          />
          <SavedIndicator
            saved={saved}
            isSaving={isSaving.current}
            saveFailed={saveFailed}
            saveFn={triggerSave}
            sx={{ marginTop: '28px' }}
          />
        </Box>

        {exceededMaxValue && <MaxValueExceededWarning warningText={maxValueExceededWarning} />}

        <Box
          style={{
            minWidth: '500px',
            maxWidth: '500px',
          }}
        >
          <Typography variant='subtitle3' color='textSecondary'>
            {question.questionNote}
          </Typography>
        </Box>
      </Box>
      <CommentsWrap question={question} />
    </Box>
  );
}

export default MoneyTypeQuestion;
